// src/pages/sessions/components/LiveSession/hooks/useMediaDiagnostics.js
//
// Monitors local audio/video tracks after publishing. Polls getStats()
// at 2s, 5s, 10s — accumulates results locally, then POSTs a summary
// to the backend at 10s. Shows up in Platform Backend logs.
//
// No database. No LiveKit metadata. Just a logged POST.

import { useEffect, useRef, useCallback } from 'react';
import { RoomEvent, Track } from 'livekit-client';
import sessionService from '@/services/sessionService';

const CHECK_TIMES = [2000, 5000, 10000];

/**
 * @param {object}  opts
 * @param {import('react').MutableRefObject} opts.roomRef
 * @param {boolean} opts.roomConnected
 * @param {string}  opts.sessionId
 * @param {string}  opts.joinPath - 'phone-skip' | 'remembered-devices' | 'prejoin'
 * @param {object}  opts.requestedDevices - { audioDeviceId, videoDeviceId }
 */
export function useMediaDiagnostics({
  roomRef,
  roomConnected,
  sessionId,
  joinPath,
  requestedDevices,
}) {
  const timersRef = useRef([]);
  const trackedRef = useRef(new Set());
  const mountedRef = useRef(true);
  const resultsRef = useRef({});
  const reportSentRef = useRef(false);

  const getTrackStats = useCallback(async (track) => {
    try {
      const sender = track.sender;
      if (!sender) return null;

      const raw = await sender.getStats();
      const out = {};

      for (const [, r] of raw) {
        if (r.type === 'media-source' && r.kind === 'audio') {
          out.audioLevel = r.audioLevel ?? null;
          out.totalAudioEnergy = r.totalAudioEnergy ?? null;
          out.totalSamplesDuration = r.totalSamplesDuration ?? null;
        }
        if (r.type === 'outbound-rtp' && r.kind === 'audio') {
          out.packetsSent = r.packetsSent ?? 0;
          out.bytesSent = r.bytesSent ?? 0;
        }
        if (r.type === 'media-source' && r.kind === 'video') {
          out.sourceWidth = r.width ?? null;
          out.sourceHeight = r.height ?? null;
          out.sourceFps = r.framesPerSecond ?? null;
        }
        if (r.type === 'outbound-rtp' && r.kind === 'video') {
          out.packetsSent = r.packetsSent ?? 0;
          out.bytesSent = r.bytesSent ?? 0;
          out.framesEncoded = r.framesEncoded ?? null;
          out.frameWidth = r.frameWidth ?? null;
          out.frameHeight = r.frameHeight ?? null;
          out.fps = r.framesPerSecond ?? null;
          out.qualityLimitation = r.qualityLimitationReason ?? null;
        }
      }
      return out;
    } catch (_) {
      return null;
    }
  }, []);

  const sendReport = useCallback(() => {
    if (reportSentRef.current || !sessionId) return;
    reportSentRef.current = true;

    const report = {
      joinPath,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      tracks: {},
    };

    for (const [trackSid, checks] of Object.entries(resultsRef.current)) {
      const info = checks._info;
      const lastCheck = checks[CHECK_TIMES[CHECK_TIMES.length - 1]];
      if (!lastCheck) continue;

      const key = info.kind === 'audio' ? 'mic' : 'cam';

      if (info.kind === 'audio') {
        const maxLevel = Math.max(
          ...CHECK_TIMES.map(t => checks[t]?.audioLevel ?? 0)
        );
        const avgPower = lastCheck.totalSamplesDuration > 0
          ? (lastCheck.totalAudioEnergy / lastCheck.totalSamplesDuration)
          : 0;

        report.tracks[key] = {
          healthy: maxLevel > 0.001 || avgPower > 0.0001,
          maxAudioLevel: Math.round(maxLevel * 10000) / 10000,
          avgPower: Math.round(avgPower * 100000) / 100000,
          packetsSent: lastCheck.packetsSent ?? 0,
          requestedDeviceId: info.requestedDeviceId,
          actualDeviceId: info.actualDeviceId,
          deviceMatch: info.deviceMatch,
          deviceLabel: info.label,
          checks: CHECK_TIMES.map(t => ({
            at: t,
            audioLevel: checks[t]?.audioLevel ?? null,
            packets: checks[t]?.packetsSent ?? null,
          })),
        };
      } else {
        const maxFps = Math.max(
          ...CHECK_TIMES.map(t => checks[t]?.fps ?? checks[t]?.sourceFps ?? 0)
        );
        const encoded = lastCheck.framesEncoded ?? 0;

        report.tracks[key] = {
          healthy: maxFps > 0 || encoded > 0,
          maxFps: Math.round(maxFps * 10) / 10,
          framesEncoded: encoded,
          resolution: lastCheck.frameWidth && lastCheck.frameHeight
            ? `${lastCheck.frameWidth}x${lastCheck.frameHeight}`
            : null,
          packetsSent: lastCheck.packetsSent ?? 0,
          requestedDeviceId: info.requestedDeviceId,
          actualDeviceId: info.actualDeviceId,
          deviceMatch: info.deviceMatch,
          deviceLabel: info.label,
          checks: CHECK_TIMES.map(t => ({
            at: t,
            fps: checks[t]?.fps ?? checks[t]?.sourceFps ?? null,
            encoded: checks[t]?.framesEncoded ?? null,
          })),
        };
      }
    }

    const micOk = report.tracks.mic?.healthy;
    const camOk = report.tracks.cam?.healthy;
    const micEmoji = micOk === true ? '✅' : micOk === false ? '❌' : '—';
    const camEmoji = camOk === true ? '✅' : camOk === false ? '❌' : '—';
    console.log(
      `[MediaDiag] Final: mic ${micEmoji} cam ${camEmoji} | joinPath=${joinPath}`,
      report
    );

    sessionService.logMediaDiagnostics(sessionId, report);
  }, [joinPath, sessionId]);

  const monitorTrack = useCallback((publication) => {
    const track = publication.track;
    if (!track) return;

    const trackSid = publication.trackSid;
    if (trackedRef.current.has(trackSid)) return;
    trackedRef.current.add(trackSid);

    const kind = track.kind;

    let actualDeviceId = null;
    let label = null;
    try {
      const settings = track.mediaStreamTrack?.getSettings?.();
      actualDeviceId = settings?.deviceId ?? null;
      label = track.mediaStreamTrack?.label ?? null;
    } catch (_) {}

    const requestedId = kind === 'audio'
      ? requestedDevices?.audioDeviceId
      : requestedDevices?.videoDeviceId;

    resultsRef.current[trackSid] = {
      _info: {
        kind,
        source: publication.source,
        requestedDeviceId: requestedId ?? null,
        actualDeviceId,
        deviceMatch: requestedId
          ? (requestedId === actualDeviceId ? true : false)
          : 'no-pref',
        label,
      },
    };

    console.log(
      `[MediaDiag] Monitoring ${kind} track ${trackSid}`,
      {
        source: publication.source,
        label,
        requestedDevice: requestedId ?? '(default)',
        actualDevice: actualDeviceId ?? '(unknown)',
        joinPath,
      }
    );

    for (const delay of CHECK_TIMES) {
      const timer = setTimeout(async () => {
        if (!mountedRef.current) return;

        const stats = await getTrackStats(track);
        if (!stats) return;

        resultsRef.current[trackSid][delay] = stats;

        if (kind === 'audio') {
          const lvl = stats.audioLevel ?? 0;
          const silent = lvl < 0.001;
          console[silent ? 'warn' : 'log'](
            `[MediaDiag] 🎤 ${delay / 1000}s: audioLevel=${lvl.toFixed(4)} pkts=${stats.packetsSent}${silent ? ' ⚠️ SILENT' : ''}`,
          );
        } else {
          const fps = stats.fps ?? stats.sourceFps ?? 0;
          const frozen = fps === 0 && (stats.framesEncoded ?? 0) === 0;
          console[frozen ? 'warn' : 'log'](
            `[MediaDiag] 📹 ${delay / 1000}s: fps=${fps} encoded=${stats.framesEncoded} res=${stats.frameWidth}x${stats.frameHeight}${frozen ? ' ⚠️ FROZEN' : ''}`,
          );
        }

        if (delay === CHECK_TIMES[CHECK_TIMES.length - 1]) {
          // Don't send here — the standalone 12s timer handles it
          // so all tracks have their 10s results collected
        }
      }, delay);

      timersRef.current.push(timer);
    }
  }, [getTrackStats, joinPath, requestedDevices, sendReport]);

  useEffect(() => {
    if (!roomConnected || !roomRef.current) return;

    const room = roomRef.current;

    const handleLocalTrackPublished = (publication) => {
      if (
        publication.source !== Track.Source.Microphone &&
        publication.source !== Track.Source.Camera
      ) return;
      monitorTrack(publication);
    };

    room.on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);

    const lp = room.localParticipant;
    if (lp) {
      for (const pub of lp.trackPublications.values()) {
        if (
          pub.track &&
          (pub.source === Track.Source.Microphone || pub.source === Track.Source.Camera)
        ) {
          monitorTrack(pub);
        }
      }
    }

    // Send report at 12s — gives all tracks time to complete their 10s checks
    // (video publishes ~0.5-1s after audio, so its 10s timer fires later)
    const reportTimer = setTimeout(() => {
      if (mountedRef.current) sendReport();
    }, 12000);
    timersRef.current.push(reportTimer);

    return () => {
      room.off(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
    };
  }, [roomConnected, roomRef, monitorTrack]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      timersRef.current.forEach(t => clearTimeout(t));
      timersRef.current = [];
    };
  }, []);
}
